import type { LogLevel } from 'livekit-client'

/**
 * livekitLogForwarding — forward livekit-client's own internal logs into the
 * audio-observability ring.
 *
 * LiveKit logs track/engine/signal-level warnings and errors that never reach
 * our telemetry — a subscription retry, an ICE restart, a decode error. These
 * are exactly the context that explains *why* a peer went dead. This installs a
 * log extension on the relevant loggers that drops warn+ messages into the ring
 * (and an optional Sentry breadcrumb), so a dead-air episode's bundle carries
 * the library's own account of what happened.
 *
 * Note: `setLogExtension` is process-wide per logger and, once set, the browser
 * console loses stacktrace info for those loggers (loglevel plugin limitation).
 * It is also **permanent**: it wraps the logger's `methodFactory` around
 * whatever is already there, so installing a no-op does not remove an earlier
 * extension, it just stacks on top of one that keeps running. Installing per
 * meeting would therefore leave every previous meeting's extension live,
 * forwarding into a ring that has been torn down.
 *
 * So the extension is installed exactly once per page and holds no meeting
 * state of its own: it writes to whichever sink is currently attached. Detach
 * clears that sink, which stops forwarding and drops the reference to the
 * meeting's ring.
 *
 * livekit-client is reached through a dynamic `import()`, and every other
 * livekit reference in this package is `import type`. That is deliberate: vite
 * forces all of firebase-repository into the eagerly-loaded `repository`
 * chunk, so one static value import from here is enough to drag livekit-client
 * (~390 kB raw) out of the lazy meeting chunks and into the chunk every page
 * load pays for — which is what pushed `repository` past the 1536 kB CI budget.
 * The cost is that forwarding starts a few microtasks late; the ring holds ~30s
 * of context, so nothing that matters is missed.
 */

/** The minimal ring surface this module writes to. */
export interface LivekitLogSink {
  push(source: string, event: string, data?: Record<string, unknown>): void
  breadcrumb?: (message: string, data?: Record<string, unknown>) => void
}

/**
 * Render a non-scalar context value as a string worth storing.
 *
 * `String(v)` is what this used to do, and on the values that matter it yields
 * `"[object Object]"`. Livekit's logs are overwhelmingly a short message plus a
 * context object carrying the detail — the participant, the track sid, the
 * error — so the old form discarded precisely the payload the ring exists to
 * keep, and did it silently.
 *
 * Errors carry nothing useful in their enumerable properties, so they are named
 * explicitly. JSON.stringify handles the rest and can throw on a cycle or a
 * BigInt; `String(v)` is then no worse than what was stored before. A stringify
 * that legitimately returns undefined (a bare function) falls back the same way.
 */
function stringifyValue(v: unknown): string {
  if (v instanceof Error) return `${v.name}: ${v.message}`.slice(0, 200)
  try {
    return (JSON.stringify(v) ?? String(v)).slice(0, 200)
  } catch {
    return String(v).slice(0, 200)
  }
}

/**
 * Whether this context is an error carrying its detail off-enumerable.
 *
 * Duck-typed rather than `instanceof Error`, because the case this exists for
 * is a `DOMException` and that only inherits from `Error.prototype` in Safari
 * 14.1+, and never across a realm boundary. A missed check does not fail
 * loudly — it falls through to the entries walk and stores `{}`, which is the
 * bug itself.
 */
function isErrorLike(v: object): v is { name: string; message: string } {
  if (v instanceof Error) return true
  const e = v as { name?: unknown; message?: unknown }
  return typeof e.name === 'string' && typeof e.message === 'string'
}

/**
 * Project a log context object down to shallow scalars for cheap, safe storage.
 *
 * The error case comes first because livekit passes the rejection itself as the
 * context, and an `Error` — a `DOMException` included — holds its name and
 * message off its enumerable own properties. Walking the entries alone stored
 * `{}`, so a refused `setSinkId` reached the ring as a message with no name on
 * it, which is the one field that says why the call failed.
 *
 * It then falls through to the walk rather than returning, because livekit's
 * own error classes carry the diagnosis in ordinary fields on top of that:
 * `ConnectionError` has `status`, `reason` and `reasonName`. Returning early
 * would trade one silently dropped payload for another.
 */
function flattenContext(context?: object): Record<string, unknown> {
  if (!context) return {}
  const out: Record<string, unknown> = {}
  let keys = 0
  const put = (k: string, v: unknown): void => {
    if (keys >= 12) return
    out[k] = v
    keys++
  }
  if (isErrorLike(context)) {
    put('name', context.name.slice(0, 200))
    put('message', context.message.slice(0, 200))
  }
  for (const [k, v] of Object.entries(context)) {
    // A plain object shaped like an error carries `name` and `message` as
    // enumerable own properties, so the walk would write them a second time.
    // The rewrite is harmless; the budget it spends is not — it can push a
    // later diagnostic field over the cap.
    if (k in out) continue
    if (keys >= 12) break
    if (v === null || v === undefined) continue
    if (
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean'
    ) {
      put(k, typeof v === 'string' ? v.slice(0, 200) : v)
    } else {
      put(k, stringifyValue(v))
    }
  }
  return out
}

/** The sink the page-wide extensions currently write to; null when detached. */
let activeSink: LivekitLogSink | null = null
/** null means "whatever livekit calls warn", resolved once the module loads. */
let activeMinLevel: LogLevel | null = null
/** Non-null once the one-per-page install has been started. */
let installPromise: Promise<void> | null = null

/**
 * Point livekit log forwarding at `sink`. Returns a detach function that stops
 * forwarding by clearing the sink — the extension itself stays registered for
 * the life of the page, because livekit offers no way to remove one.
 *
 * Registering the extensions needs livekit-client, which loads asynchronously,
 * so the sink is swapped in immediately and the registration lands on a later
 * tick. Await {@link livekitLogForwardingReady} to observe it.
 */
export function installLivekitLogForwarding(
  sink: LivekitLogSink,
  minLevel?: LogLevel
): () => void {
  activeSink = sink
  activeMinLevel = minLevel ?? null
  installPromise ??= installExtensions().catch(() => {
    // A failed chunk load costs the livekit-internal log context and nothing
    // else — every other signal the detector reads is unaffected — so it must
    // not surface as an unhandled rejection.
  })

  return () => {
    // Only clear our own sink: a later meeting may already have attached its
    // own, and this detach must not silence it.
    if (activeSink === sink) activeSink = null
  }
}

/** Resolves once the page-wide extensions are registered (or failed to be). */
export function livekitLogForwardingReady(): Promise<void> {
  return installPromise ?? Promise.resolve()
}

/** Register the page-wide extensions. Runs at most once — see the note above. */
async function installExtensions(): Promise<void> {
  const { LogLevel, LoggerNames, getLogger, setLogExtension } =
    await import('livekit-client')

  /** Loggers whose warnings/errors are worth keeping for a dead-air bundle. */
  const forwardedLoggers = [
    LoggerNames.Track,
    LoggerNames.Engine,
    LoggerNames.Signal,
    // `Room` carries `detected connection state mismatch`, which livekit's own
    // watchdog logs every 4s while `verifyTransport()` fails and which tears
    // the room down on the third one. Without it a bundle can show a subscriber
    // PC reading `disconnected` with no way to tell whether livekit had noticed
    // — and so no way to tell a repair that was already seconds from running
    // from one that was never coming. Low volume: it is warn-and-above only,
    // and this line is the only recurring warning the logger emits.
    LoggerNames.Room,
  ]

  for (const name of forwardedLoggers) {
    setLogExtension((level, msg, context) => {
      const target = activeSink
      if (!target) return
      if (level < (activeMinLevel ?? LogLevel.warn)) return
      const data = {
        logger: name,
        level: LogLevel[level],
        msg,
        ...flattenContext(context),
      }
      // Never let forwarding throw. This runs inside livekit's own `log.warn`,
      // and livekit puts its recovery right after the warning: `websocket
      // closed` is followed by `handleOnClose`, and `detected connection state
      // mismatch` by `engine.reconnect()`. A throw here skips both, so the
      // room sits "connected" on a dead signal socket and never reconnects.
      // Under E2E, where Sentry v7 is not initialised, `addBreadcrumb` throws
      // on the v8 carrier on every call — which is how this was found.
      try {
        target.push('livekit_log', LogLevel[level] ?? 'log', data)
        target.breadcrumb?.(`livekit:${name} ${msg}`, data)
      } catch {
        // Losing one breadcrumb is the whole cost.
      }
    }, getLogger(name))
  }
}

/** Test-only: forget the page-wide install so each test starts clean. */
export function __resetLivekitLogForwardingForTests(): void {
  activeSink = null
  activeMinLevel = null
  installPromise = null
}
